import React from 'react';
import { motion } from 'framer-motion';
import { ChartBarIcon, ArrowTrendingUpIcon } from '@heroicons/react/24/outline';
import { TeaserDemoProps } from '../../config/teaserContent';
import { createOptimizedTeaserDemo, useTeaserDemoState } from './baseTeaserDemo';

interface HistoricalProjectionDemoProps extends TeaserDemoProps {
  projection?: {
    startValue: number;
    endValue: number;
    dataPoints: Array<{ x: number; y: number; label: string }>;
    profitMetrics: {
      totalProfit: number;
      profitPercentage: number;
      annualizedReturn: number;
    };
  };
}

const HistoricalProjectionDemoComponent: React.FC<HistoricalProjectionDemoProps> = ({ 
  className, 
  aspectRatio = 'video',
  projection = {
    startValue: 50000,
    endValue: 127500,
    dataPoints: [
      { x: 0, y: 50000, label: 'Start' },
      { x: 25, y: 62000, label: '6M' },
      { x: 50, y: 78000, label: '1Y' },
      { x: 75, y: 98000, label: '18M' },
      { x: 100, y: 127500, label: '2Y' }
    ],
    profitMetrics: {
      totalProfit: 77500,
      profitPercentage: 155,
      annualizedReturn: 28.7
    }
  }
}) => {
  const { containerClass } = useTeaserDemoState(aspectRatio);

  const formatCurrency = (amount: number) => {
    return `$${(amount / 1000).toFixed(0)}k`;
  };

  // Create SVG path for the projection line
  const createPath = (points: Array<{ x: number; y: number }>) => {
    const viewBoxHeight = 60;
    
    // Normalize Y values to fit in viewBox
    const minY = Math.min(...points.map(p => p.y));
    const maxY = Math.max(...points.map(p => p.y));
    const yRange = maxY - minY;
    
    const pathData = points.map((point, index) => {
      const x = point.x;
      const y = viewBoxHeight - ((point.y - minY) / yRange) * (viewBoxHeight - 10) - 5;
      
      return index === 0 ? `M ${x} ${y}` : `L ${x} ${y}`;
    }).join(' ');
    
    return pathData;
  };

  return (
    <motion.div 
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.3 }}
      className={`relative p-4 h-full flex flex-col justify-between ${containerClass} ${className}`}
    >
      {/* Header */}
      <div className="flex items-center gap-3 mb-4">
        <motion.div
          animate={{ 
            boxShadow: [
              '0 0 10px rgba(16, 185, 129, 0.3)',
              '0 0 20px rgba(16, 185, 129, 0.6)',
              '0 0 10px rgba(16, 185, 129, 0.3)'
            ]
          }}
          transition={{ duration: 2, repeat: Infinity }}
          className="w-8 h-8 bg-gradient-to-r from-green-500 to-emerald-500 rounded-full flex items-center justify-center shadow-lg"
        >
          <ChartBarIcon className="w-4 h-4 text-white" />
        </motion.div>
        <div>
          <h4 className="text-white font-semibold text-sm">Historical Projection</h4>
          <p className="text-gray-300 text-xs">2-year forecast model</p>
        </div>
      </div>

      {/* Chart Visualization */}
      <div className="relative bg-black/20 rounded-lg p-3 mb-4 flex-1">
        <svg
          className="w-full h-full"
          viewBox="0 0 100 60"
          preserveAspectRatio="none"
        >
          {/* Grid lines */}
          <defs>
            <pattern id="grid" width="20" height="15" patternUnits="userSpaceOnUse">
              <path d="M 20 0 L 0 0 0 15" fill="none" stroke="rgba(255,255,255,0.1)" strokeWidth="0.5"/>
            </pattern>
          </defs>
          <rect width="100" height="60" fill="url(#grid)" />

          {/* Animated projection line */}
          <motion.path
            d={createPath(projection.dataPoints)}
            fill="none"
            stroke="url(#gradient)"
            strokeWidth="2"
            initial={{ pathLength: 0 }}
            animate={{ pathLength: 1 }}
            transition={{ duration: 3, ease: "easeInOut" }}
          />

          {/* Gradient definition */}
          <defs>
            <linearGradient id="gradient" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="#10b981" />
              <stop offset="50%" stopColor="#3b82f6" />
              <stop offset="100%" stopColor="#8b5cf6" />
            </linearGradient>
          </defs>

          {/* Data points */}
          {projection.dataPoints.map((point, index) => (
            <motion.circle
              key={index}
              cx={point.x}
              cy={60 - ((point.y - projection.startValue) / (projection.endValue - projection.startValue)) * 50 - 5}
              r="2"
              fill="#10b981"
              initial={{ opacity: 0, scale: 0 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: index * 0.5, duration: 0.5 }}
            />
          ))}
        </svg>

        {/* Value labels */}
        <div className="absolute top-2 left-2 text-xs text-green-300 font-medium">
          {formatCurrency(projection.startValue)}
        </div>
        <div className="absolute top-2 right-2 text-xs text-green-300 font-medium">
          {formatCurrency(projection.endValue)}
        </div>
      </div>

      {/* Profit Metrics */}
      <div className="grid grid-cols-3 gap-2 mb-3">
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.5, duration: 0.5 }}
          className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-lg p-2 text-center"
        >
          <motion.p
            animate={{ scale: [1, 1.1, 1] }}
            transition={{ duration: 1.5, repeat: Infinity }}
            className="text-green-400 font-bold text-sm"
          >
            {formatCurrency(projection.profitMetrics.totalProfit)}
          </motion.p>
          <p className="text-xs text-gray-400">Total Profit</p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.7, duration: 0.5 }}
          className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-lg p-2 text-center"
        >
          <motion.p
            animate={{ scale: [1, 1.1, 1] }}
            transition={{ duration: 1.5, repeat: Infinity, delay: 0.3 }}
            className="text-blue-400 font-bold text-sm"
          >
            +{projection.profitMetrics.profitPercentage}%
          </motion.p>
          <p className="text-xs text-gray-400">Growth</p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.9, duration: 0.5 }}
          className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-lg p-2 text-center"
        >
          <motion.p
            animate={{ scale: [1, 1.1, 1] }}
            transition={{ duration: 1.5, repeat: Infinity, delay: 0.6 }}
            className="text-purple-400 font-bold text-sm"
          >
            {projection.profitMetrics.annualizedReturn}%
          </motion.p>
          <p className="text-xs text-gray-400">Annual</p>
        </motion.div>
      </div>

      {/* Projection Indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2.5, duration: 0.5 }}
        className="text-center"
      >
        <motion.p
          animate={{ opacity: [0.7, 1, 0.7] }}
          transition={{ duration: 2, repeat: Infinity }}
          className="text-emerald-300 text-xs font-medium flex items-center justify-center gap-1"
        >
          <ArrowTrendingUpIcon className="w-3 h-3" />
          Statistical projection model
        </motion.p>
      </motion.div>
    </motion.div>
  );
};

export const HistoricalProjectionDemo = createOptimizedTeaserDemo(
  HistoricalProjectionDemoComponent,
  'HistoricalProjectionDemo'
);